import type { FormSelectOption } from '@asseco/react-native-gov-components';
import type { PhoneNumberWithCountryCode } from '@gov-design-system-ce/react-form';
import type { CiselnikyCiselnikPolozkaDto } from '@gov-nx/api';
import {
	getPhoneCountryCodesQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import type { LocalizationLanguage } from '@gov-nx/core/app';
import type { Optional } from '@gov-nx/core/types';
import { has } from '@gov-nx/core/types';
import { digitsOnly } from '@gov-nx/utils/common';

const stringToPhoneShape =
	(countryCodes: CiselnikyCiselnikPolozkaDto[]) =>
	(phone?: string): Optional<PhoneNumberWithCountryCode> => {
		if (!phone) return;
		if (!phone.startsWith('+')) {
			return {
				countryCode: '420',
				number: phone,
			};
		}
		const digits = digitsOnly(phone);
		const countryCode = countryCodes.find(
			(code) => code.kod && digits.startsWith(code.kod)
		);
		if (!countryCode?.kod) {
			return;
		}

		return {
			countryCode: countryCode.kod,
			number: digits.slice(countryCode.kod.length),
		};
	};

const getOptions =
	(countryCodes: CiselnikyCiselnikPolozkaDto[]) =>
	(appLanguage: LocalizationLanguage): FormSelectOption[] =>
		countryCodes.filter(has('kod')).map((code) => ({
			value: code.kod,
			label:
				appLanguage === 'cs'
					? `${code.nazevCs} (+${code.kod})`
					: `${code.nazevEn} (+${code.kod})`,
		}));

export const useCountryCodes = () => {
	const { axiosInstance } = useAxiosInstance();
	const query = usePoQuery(getPhoneCountryCodesQuery(axiosInstance));
	const countryCodes = query.data?.seznam ?? [];

	return {
		countryCodes,
		getOptions: getOptions(countryCodes),
		isFetched: query.isFetched,
		isLoading: query.isLoading,
		error: query.error,
		stringToPhoneShape: stringToPhoneShape(countryCodes),
	};
};
